import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import './index.css'
import { AuthProvider } from './lib/auth'
import App from './App'
import { ErrorBoundary } from './components/ErrorBoundary'
import { installGlobalHandlers } from './lib/logger'
import { queryClient } from './lib/queryClient'
import { initTelemetry } from './lib/telemetry'
import { initAnalytics } from './lib/analytics'
import { JotaiProvider, jotaiStore } from './lib/jotaiStore'

// Initialise Sentry before any other code runs so its global error hooks
// catch boot-time crashes. No-op on localhost (gated by VITE_SENTRY_DSN +
// VITE_ENABLE_TELEMETRY). See docs/adr/0006-sentry-telemetry.md.
initTelemetry()

// Initialise Posthog. Same gate as Sentry (VITE_POSTHOG_KEY +
// VITE_ENABLE_TELEMETRY both required). See docs/adr/0014-posthog-analytics.md.
initAnalytics()

// Capture errors that escape React (async handlers, unhandled promise
// rejections, native event callbacks). Call before rendering so handlers are
// live during initial mount.
installGlobalHandlers()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary name="root">
      {/* BrowserRouter must wrap everything that might call `useNavigate` /
          `useLocation`. Keep it OUTSIDE the AuthProvider so the auth
          flow's post-sign-in redirects can use the router without a
          chicken-and-egg. ADR 0026 has the full route-shape decision. */}
      <BrowserRouter>
        <JotaiProvider store={jotaiStore}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <App />
            </AuthProvider>
          </QueryClientProvider>
        </JotaiProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
)
